import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useNav } from '../nav/NavContext.js'

type ColType = 'text' | 'money' | 'number' | 'percent' | 'date' | 'datetime'
interface Column {
  key: string
  label: string
  type?: ColType
}
interface Drill {
  report: string
  param: FilterKey
  column: string
  label: string
}
type FilterKey = 'branch' | 'from' | 'to' | 'customer' | 'vehicle' | 'broker' | 'vendor' | 'driver' | 'status'

interface ReportSummary {
  key: string
  category: string
  title: string
  description: string
  filters: FilterKey[]
  required: FilterKey[]
  status_options: string[] | null
  basis: string | null
}
interface Catalog {
  categories: Array<{ name: string; reports: ReportSummary[] }>
  total: number
}
interface ReportResult extends ReportSummary {
  columns: Column[]
  drill: Drill | null
  note: string | null
  totals: Record<string, string> | null
  total_rows: number
  truncated: boolean
  rows: Array<Record<string, unknown>>
  generated_at: string
}
interface NamedRow {
  id: string
  name: string
}

/** Query-string name the API expects for each filter. */
const PARAM: Record<FilterKey, string> = {
  branch: 'branch_id',
  from: 'from',
  to: 'to',
  customer: 'customer_id',
  broker: 'broker_id',
  vendor: 'vendor_id',
  driver: 'driver_id',
  vehicle: 'vehicle',
  status: 'status',
}

const LOOKUP: Partial<Record<FilterKey, string>> = {
  customer: '/customers?pageSize=100',
  broker: '/brokers?pageSize=100',
  vendor: '/vendors?pageSize=100',
  driver: '/drivers?pageSize=100',
}

const LABEL: Record<FilterKey, string> = {
  branch: 'Branch',
  from: 'From',
  to: 'To',
  customer: 'Customer',
  vehicle: 'Vehicle no.',
  broker: 'Broker',
  vendor: 'Vendor',
  driver: 'Driver',
  status: 'Status',
}

function formatCell(value: unknown, type: ColType | undefined): string {
  if (value == null || value === '') return ''
  if (type === 'money') return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (type === 'number') return Number(value).toLocaleString('en-IN')
  if (type === 'percent') return `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`
  return String(value)
}

const isNumeric = (c: Column): boolean => c.type === 'money' || c.type === 'number' || c.type === 'percent'

/**
 * Stage 11 §2: one framework for every report — category tabs, a report list,
 * the shared filter bar, the result table with totals and drill-down, and
 * CSV / Excel / PDF export of exactly the filtered rows. Everything about a
 * report (its filters, columns, drill link, note) comes from the API, so a new
 * report needs no new screen.
 */
export function ReportsPage() {
  const { branches, isAllBranches } = useAuth()
  const { params } = useNav()
  const deepLinked = useRef(false)
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [category, setCategory] = useState<string>('')
  const [selected, setSelected] = useState<ReportSummary | null>(null)
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ReportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [lookups, setLookups] = useState<Partial<Record<FilterKey, NamedRow[]>>>({})

  useEffect(() => {
    void api
      .get<Catalog>('/reports')
      .then((c) => {
        setCatalog(c)
        setCategory(c.categories[0]?.name ?? '')
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not load reports'))
  }, [])

  // Deep link (?view=reports&report=key, e.g. from the WhatsApp summary): open that report once.
  useEffect(() => {
    if (deepLinked.current || !catalog || !params.report) return
    deepLinked.current = true
    for (const c of catalog.categories) {
      const def = c.reports.find((r) => r.key === params.report)
      if (def) {
        setCategory(c.name)
        open(def)
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, params.report])

  // Lookup lists are fetched once per filter kind, on demand.
  const needed = useMemo(() => new Set(selected?.filters ?? []), [selected])
  useEffect(() => {
    for (const kind of Object.keys(LOOKUP) as FilterKey[]) {
      if (needed.has(kind) && !lookups[kind]) {
        void api
          .get<{ rows: NamedRow[] }>(LOOKUP[kind]!)
          .then((r) => setLookups((prev) => ({ ...prev, [kind]: r.rows })))
          .catch(() => setLookups((prev) => ({ ...prev, [kind]: [] })))
      }
    }
  }, [needed, lookups])

  const queryString = useCallback(
    (def: ReportSummary, values: Record<string, string>, extra?: Record<string, string>): string => {
      const p = new URLSearchParams()
      for (const k of def.filters) {
        const v = values[k]
        if (v) p.set(PARAM[k], v)
      }
      for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v)
      const s = p.toString()
      return s ? `?${s}` : ''
    },
    [],
  )

  const run = useCallback(
    async (def: ReportSummary, values: Record<string, string>) => {
      const missing = def.required.find((k) => !values[k])
      if (missing) {
        setResult(null)
        setError(`Choose a ${LABEL[missing].toLowerCase()} to run this report.`)
        return
      }
      setLoading(true)
      setError(null)
      try {
        setResult(await api.get<ReportResult>(`/reports/${def.key}${queryString(def, values)}`))
      } catch (e) {
        setResult(null)
        setError(e instanceof ApiError ? e.message : 'Could not run this report')
      } finally {
        setLoading(false)
      }
    },
    [queryString],
  )

  function open(def: ReportSummary, carry: Record<string, string> = {}) {
    // Keep the period and branch while switching reports; entity filters belong to one report.
    const next: Record<string, string> = {}
    for (const k of ['branch', 'from', 'to'] as const) if (filters[k]) next[k] = filters[k]
    Object.assign(next, carry)
    setSelected(def)
    setFilters(next)
    setResult(null)
    if (def.required.length === 0 || def.required.every((k) => next[k])) void run(def, next)
    else setError(null)
  }

  function drillInto(drill: Drill, row: Record<string, unknown>) {
    const target = catalog?.categories.flatMap((c) => c.reports).find((r) => r.key === drill.report)
    const value = row[drill.column]
    if (!target || value == null) return
    setCategory(target.category)
    open(target, { [drill.param]: String(value) })
  }

  async function exportAs(format: 'csv' | 'xlsx' | 'pdf') {
    if (!selected) return
    try {
      const { blob, filename } = await api.download(`/reports/${selected.key}/export${queryString(selected, filters, { format })}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Export failed')
    }
  }

  const visible = catalog?.categories.find((c) => c.name === category)?.reports ?? []
  const branchChoices = isAllBranches ? branches : branches.length > 1 ? branches : []

  if (!catalog) return <p>{error ?? 'Loading reports…'}</p>
  if (catalog.total === 0) return <p>You don’t have access to any reports.</p>

  return (
    <div>
      <nav className="tf-tabs" style={{ marginBottom: '1rem' }}>
        {catalog.categories.map((c) => (
          <button
            key={c.name}
            type="button"
            className={category === c.name ? 'tf-tab-active' : ''}
            onClick={() => setCategory(c.name)}
          >
            {c.name} ({c.reports.length})
          </button>
        ))}
      </nav>

      <div className="tf-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.5rem' }}>
          {visible.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => open(r)}
              className={selected?.key === r.key ? 'tf-tab-active' : ''}
              style={{ textAlign: 'left', padding: '0.6rem 0.75rem', cursor: 'pointer' }}
            >
              <strong>{r.title}</strong>
              <br />
              <small>{r.description}</small>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="tf-card">
          <h3 style={{ marginTop: 0 }}>
            {selected.title}{' '}
            {selected.basis && <small style={{ fontWeight: 'normal' }}>({selected.basis} basis)</small>}
          </h3>

          <form
            className="tf-form"
            style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}
            onSubmit={(e) => {
              e.preventDefault()
              void run(selected, filters)
            }}
          >
            {selected.filters.map((k) => {
              const value = filters[k] ?? ''
              const set = (v: string) => setFilters((prev) => ({ ...prev, [k]: v }))
              if (k === 'branch') {
                if (branchChoices.length === 0) return null
                return (
                  <label key={k} className="tf-field">
                    <span>{LABEL[k]}</span>
                    <select value={value} onChange={(e) => set(e.target.value)}>
                      <option value="">{isAllBranches ? 'All branches' : 'Current branch'}</option>
                      {branchChoices.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              }
              if (k === 'from' || k === 'to') {
                return (
                  <label key={k} className="tf-field">
                    <span>{LABEL[k]}</span>
                    <input type="date" value={value} onChange={(e) => set(e.target.value)} />
                  </label>
                )
              }
              if (k === 'status') {
                return (
                  <label key={k} className="tf-field">
                    <span>{LABEL[k]}</span>
                    <select value={value} onChange={(e) => set(e.target.value)}>
                      <option value="">All</option>
                      {(selected.status_options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              }
              if (k === 'vehicle') {
                return (
                  <label key={k} className="tf-field">
                    <span>{LABEL[k]}</span>
                    <input value={value} onChange={(e) => set(e.target.value)} placeholder="e.g. TN45GH4321" />
                  </label>
                )
              }
              const options = lookups[k] ?? []
              return (
                <label key={k} className="tf-field">
                  <span>
                    {LABEL[k]}
                    {selected.required.includes(k) ? ' *' : ''}
                  </span>
                  <select value={value} onChange={(e) => set(e.target.value)}>
                    <option value="">{selected.required.includes(k) ? 'Select…' : 'All'}</option>
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
              )
            })}
            <button type="submit" disabled={loading}>
              {loading ? 'Running…' : 'Run report'}
            </button>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button type="button" disabled={!result} onClick={() => void exportAs('csv')}>CSV</button>
              <button type="button" disabled={!result} onClick={() => void exportAs('xlsx')}>Excel</button>
              <button type="button" disabled={!result} onClick={() => void exportAs('pdf')}>PDF</button>
            </span>
          </form>

          {error && <p role="alert" style={{ color: 'var(--tf-danger, #b42318)' }}>{error}</p>}
          {result?.note && (
            <p style={{ background: 'var(--tf-note-bg, #f4f7fb)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>{result.note}</p>
          )}

          {result && (
            <>
              <p style={{ margin: '0 0 0.5rem' }}>
                <small>
                  {result.total_rows} row{result.total_rows === 1 ? '' : 's'}
                  {result.truncated ? ` — showing the first ${result.rows.length}; exports include all` : ''}
                </small>
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="tf-table">
                  <thead>
                    <tr>
                      {result.columns.map((c) => (
                        <th key={c.key} style={isNumeric(c) ? { textAlign: 'right' } : undefined}>
                          {c.label}
                        </th>
                      ))}
                      {result.drill && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i}>
                        {result.columns.map((c) => (
                          <td key={c.key} style={isNumeric(c) ? { textAlign: 'right' } : undefined}>
                            {formatCell(row[c.key], c.type)}
                          </td>
                        ))}
                        {result.drill && (
                          <td>
                            {row[result.drill.column] != null && (
                              <button type="button" onClick={() => drillInto(result.drill!, row)}>
                                {result.drill.label} ›
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {result.rows.length === 0 && (
                      <tr>
                        <td colSpan={result.columns.length + (result.drill ? 1 : 0)}>No rows for these filters.</td>
                      </tr>
                    )}
                  </tbody>
                  {result.totals && (
                    <tfoot>
                      <tr>
                        {result.columns.map((c, i) => (
                          <td key={c.key} style={{ fontWeight: 600, ...(isNumeric(c) ? { textAlign: 'right' } : {}) }}>
                            {i === 0 ? 'Total' : result.totals![c.key] != null ? formatCell(result.totals![c.key], c.type) : ''}
                          </td>
                        ))}
                        {result.drill && <td />}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
